import { mergeAt, type CellRange, type SheetLayout } from './layout';
import { formatNumber, isDateFormat } from './parser/format';
import type { SheetCell } from './parser';

export interface CellPoint {
  row: number;
  col: number;
}

export function cellRect(layout: SheetLayout, point: CellPoint): CellRange {
  const merge = mergeAt(layout, point.row, point.col);
  if (merge) {
    return { rowFrom: merge.rowFrom, rowTo: merge.rowTo, colFrom: merge.colFrom, colTo: merge.colTo };
  }
  return { rowFrom: point.row, rowTo: point.row, colFrom: point.col, colTo: point.col };
}

export function rangeBetween(layout: SheetLayout, anchor: CellPoint, focus: CellPoint): CellRange {
  const range: CellRange = {
    rowFrom: Math.min(anchor.row, focus.row),
    rowTo: Math.max(anchor.row, focus.row),
    colFrom: Math.min(anchor.col, focus.col),
    colTo: Math.max(anchor.col, focus.col),
  };
  let changed = true;
  while (changed) {
    changed = false;
    layout.sheet.merges.forEach((merge) => {
      const intersects =
        merge.rowFrom <= range.rowTo &&
        merge.rowTo >= range.rowFrom &&
        merge.colFrom <= range.colTo &&
        merge.colTo >= range.colFrom;
      const inside =
        merge.rowFrom >= range.rowFrom &&
        merge.rowTo <= range.rowTo &&
        merge.colFrom >= range.colFrom &&
        merge.colTo <= range.colTo;
      if (!intersects || inside) {
        return;
      }
      range.rowFrom = Math.min(range.rowFrom, merge.rowFrom);
      range.rowTo = Math.max(range.rowTo, merge.rowTo);
      range.colFrom = Math.min(range.colFrom, merge.colFrom);
      range.colTo = Math.max(range.colTo, merge.colTo);
      changed = true;
    });
  }
  return range;
}

function quote(text: string): string {
  return /[\t\n"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function rangeText(layout: SheetLayout, range: CellRange): string {
  const { sheet } = layout;
  const lines: string[] = [];
  const rowTo = Math.min(range.rowTo, Math.max(layout.usedRows - 1, range.rowFrom));
  const colTo = Math.min(range.colTo, Math.max(layout.usedColumns - 1, range.colFrom));
  for (let row = range.rowFrom; row <= rowTo; row++) {
    if (sheet.hiddenRows[row]) {
      continue;
    }
    const values: string[] = [];
    for (let col = range.colFrom; col <= colTo; col++) {
      if (sheet.hiddenColumns[col]) {
        continue;
      }
      const merge = mergeAt(layout, row, col);
      const covered = merge && (merge.rowFrom !== row || merge.colFrom !== col);
      values.push(covered ? '' : quote(sheet.rows[row]?.[col]?.text ?? ''));
    }
    lines.push(values.join('\t'));
  }
  return `${lines.join('\r\n')}\r\n`;
}

export function copyText(text: string): void {
  const handler = (event: ClipboardEvent) => {
    event.clipboardData?.setData('text/plain', text);
    event.preventDefault();
  };
  document.addEventListener('copy', handler);
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }
  document.removeEventListener('copy', handler);
  if (!copied) {
    navigator.clipboard?.writeText(text).catch(() => undefined);
  }
}

export function cellText(cell: SheetCell | undefined, date1904: boolean): string {
  if (!cell || cell.kind === 'empty') {
    return '';
  }
  if (cell.formula) {
    return cell.formula;
  }
  if (cell.kind !== 'number' || typeof cell.value !== 'number') {
    return typeof cell.value === 'string' ? cell.value : cell.text;
  }

  const value = cell.value;
  const code = cell.formatCode ?? '';
  if (isDateFormat(code)) {
    const hasDate = value >= 1;
    const hasTime = Math.abs(value - Math.floor(value)) > 1e-9;
    const pattern = [hasDate ? 'dd.mm.yyyy' : '', hasTime || !hasDate ? 'h:mm:ss' : ''].filter(Boolean).join(' ');
    return formatNumber(value, pattern, date1904).text;
  }
  const plain = (number: number) => String(Number(number.toPrecision(15))).replace('.', ',');
  if (code.replace(/"[^"]*"/g, '').includes('%')) {
    return `${plain(value * 100)}%`;
  }
  return plain(value);
}

export interface SelectionView {
  range: CellRange;
  active: CellRange;
  copied: CellRange | null;
}
